import type { WallDatumAbstraction } from "building-types/datums/datumAbstractionTypes";
import type { Facade, FacadeSpanStratum, FacadeSweepStratum } from "building-types/facades/facadeTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { HalfEdgeAssociationTable } from "building-types/planarGraphs/planarGraphAssociationTypes";
import { PathPriority } from "building-types/walls/pathPriority";
import type {
  WallAssemblyLayerOperator,
  WallAssemblyLayerOperatorAdd,
  WallAssemblyLayerOperatorOverride,
  WallAssemblyLayerOperatorRemove,
  WallAssemblyOffsetOperator,
  WallAssemblyOperator,
  WallFoundationInteractionOperator,
  WallOperatorSetTopDatum,
} from "building-types/walls/wallAssemblyOverrideTypes";
import type {
  WallAssembly,
  WallAssemblyLayer,
  WallAssemblyStructuralLayer,
} from "building-types/walls/wallAssemblyTypes";
import type {
  WallDescriptor,
  WallOperators,
  WallSection,
  WallSideRoomDescriptor,
} from "building-types/walls/wallDesciptorTypes";
import type { StructuralMaterialTable } from "building-types/walls/wallMaterialTypes";
import { WallMaterialType } from "building-types/walls/wallMaterialTypesEnum";
import type { WallPath } from "building-types/walls/wallTypes";
import type { Table } from "common/data/table";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { docEntityGetSortedChildrenFromTable } from "doc/entity/docEntityGetChildrenFromTable";
import { DIST_EPSILON } from "geom/core";
import { planarGraphAdjacentHalfEdge } from "geom/planarGraph";
import { collectWallFacadeDatums } from "../../utils/facade";
import type { WallDatumAbstractionProvider } from "../datums/datumAbstractionProviders";
import {
  accumWallAssemblyLayerThickness,
  wallAssemblyCanApplyLayer,
  wallAssemblyExteriorFinishLayerNew,
  wallAssemblyLayerNewFromConstructor,
  wallAssemblyNew,
} from "./wallAssembly";
import { wallAssemblyDatumsFromFacadeSpan } from "./wallAssemblyDefaults";

export function wallAssemblyOffsetOperatorNew(offset: number): WallAssemblyOffsetOperator {
  return {
    operationType: "OFFSET",
    offset,
  };
}

export function isWallAssemblyOffsetOperator(operator: WallAssemblyOperator): operator is WallAssemblyOffsetOperator {
  return operator.operationType === "OFFSET";
}

export function wallOperatorSetTopDatumNew(offset: number): WallOperatorSetTopDatum {
  return {
    operationType: "WALL_SET_TOP_DATUM",
    offset,
  };
}

export function isWallOperatorSetTopDatum(operator: WallAssemblyOperator): operator is WallOperatorSetTopDatum {
  return operator.operationType === "WALL_SET_TOP_DATUM";
}

export function wallFoundationInteractionOperatorNew(
  foundationIds: readonly string[] = [],
): WallFoundationInteractionOperator {
  return {
    operationType: "FOUNDATION_INTERACTION",
    foundationIds,
  };
}

export function isWallFoundationInteractionOperator(
  operator: WallAssemblyOperator,
): operator is WallFoundationInteractionOperator {
  return operator.operationType === "FOUNDATION_INTERACTION";
}

export function isWallAssemblyLayerOperator(operator: WallAssemblyOperator): operator is WallAssemblyLayerOperator {
  return (
    !isWallAssemblyOffsetOperator(operator) &&
    !isWallOperatorSetTopDatum(operator) &&
    !isWallFoundationInteractionOperator(operator)
  );
}

export function wallAssemblyLayerOperatorOverrideNew(
  layer: WallAssemblyLayerOperatorOverride["layer"],
  layerIndex = 0,
): WallAssemblyLayerOperatorOverride {
  return {
    operationType: "OVERRIDE",
    layerIndex,
    layer,
  };
}

function isWallAssemblyLayerOverrideOperator(
  operator: WallAssemblyOperator,
): operator is WallAssemblyLayerOperatorOverride {
  return operator.operationType === "OVERRIDE";
}

export function wallAssemblyLayerOperatorAddNew(
  layer: WallAssemblyLayerOperatorAdd["layer"],
): WallAssemblyLayerOperatorAdd {
  return {
    operationType: "ADD",
    layer,
  };
}

function isWallAssemblyLayerAddOperator(operator: WallAssemblyOperator): operator is WallAssemblyLayerOperatorAdd {
  return operator.operationType === "ADD";
}

export function wallAssemblyLayerOperatorRemoveNew(
  layer: WallAssemblyLayerOperatorRemove["layer"],
  layerIndex = 0,
): WallAssemblyLayerOperatorRemove {
  return {
    operationType: "REMOVE",
    layerIndex,
    layer,
  };
}

export function isWallAssemblyLayerRemoveOperator(
  operator: WallAssemblyOperator,
): operator is WallAssemblyLayerOperatorRemove {
  return operator.operationType === "REMOVE";
}

function isWallAssemblyCoreOperator(operator: WallAssemblyOperator) {
  return (
    isWallOperatorSetTopDatum(operator) ||
    isWallAssemblyOffsetOperator(operator) ||
    isWallFoundationInteractionOperator(operator) ||
    operator.layer.type === WallMaterialType.FOUNDATION ||
    operator.layer.type === WallMaterialType.STRUCTURE ||
    operator.layer.type === WallMaterialType.SUBSTRATE
  );
}

export function collectHalfEdgeOperators(
  leftHalfEdge: number,
  leftWallRoom: WallSideRoomDescriptor,
  rightWallRoom: WallSideRoomDescriptor,
  wallAssemblyOperators: HalfEdgeAssociationTable<WallAssemblyOperator>,
  leftWallFacades: Facade[],
  rightWallFacades: Facade[],
  wallDatums: Table<WallDatumAbstraction>,
  facadeSpanStratum: Table<FacadeSpanStratum>,
  facadeSweepStratum: Table<FacadeSweepStratum>,
  componentInstanceChain?: string[],
): WallOperators {
  const rightHalfEdge = planarGraphAdjacentHalfEdge(leftHalfEdge);

  const leftCoreOperators: WallAssemblyOperator[] = [];
  const leftFinishOperators: WallAssemblyOperator[] = [];
  // Overrides from the opposite side - need to be applied first
  const leftCoreOperatorsFromRight: WallAssemblyOperator[] = [];
  const leftHalfEdgeOperatorsFromRight: WallAssemblyOperator[] = [];

  const rightCoreOperators: WallAssemblyOperator[] = [];
  const rightFinishOperators: WallAssemblyOperator[] = [];
  // Overrides from the opposite side - need to be applied first
  const rightCoreOperatorsFromLeft: WallAssemblyOperator[] = [];
  const rightFinishOperatorsFromLeft: WallAssemblyOperator[] = [];

  /**
   * We add all the operators to the half edge in reverse order of precedence.
   * (i.e the operators will be applied in the order that they are added to the
   * array, so the higher indices will override lower indices ):
   *
   * 1. Operators assigned at the `Room` level. These are typically interior wall material
   * overrides and can only be assigned to the `left/interior` side of the wall
   *
   * 2. Operators assigned at the `RoomEdge` level. These are `EdgeBehaviors` and can be assigned
   * to the `left/interior` or `right/exterior` side of the wall.
   * In the event that an operator is assigned to the `exterior` side of the wall, it must take less
   * precedence than any override assigned by the opposite half edge to that side
   *
   * 3. Operators assigned from the `FacadeSpans`. These take the highest precedence and are
   * applied last.
   */

  /**
   * 1. Add all the `Room` operators - these are generally from settings
   * and are the first layer applied beyond the WALL_CONDITION layers.
   * @NOTE - these are only applicable to the side of the wall that they are assigned to.
   */

  for (const operator of leftWallRoom.roomWallOperators) {
    if (isWallAssemblyCoreOperator(operator)) {
      leftCoreOperators.push(operator);
    } else {
      leftFinishOperators.push(operator);
    }
  }

  for (const operator of rightWallRoom.roomWallOperators) {
    if (isWallAssemblyCoreOperator(operator)) {
      rightCoreOperators.push(operator);
    } else {
      rightFinishOperators.push(operator);
    }
  }

  /**
   * 2. Add all the `Room Edge` operators.
   * @NOTE - Room Edge operators can address either side of the halfEdge, since they can be
   * flagged as `outside`.
   */

  const leftHalfEdgeOperators = wallAssemblyOperators[leftHalfEdge] ?? [];
  if (leftHalfEdgeOperators.length) {
    for (const leftHalfEdgeOperator of leftHalfEdgeOperators) {
      const operator = leftHalfEdgeOperator.item;
      if (leftHalfEdgeOperator.outside) {
        if (isWallAssemblyCoreOperator(operator)) {
          rightCoreOperatorsFromLeft.push(operator);
        } else {
          rightFinishOperatorsFromLeft.push(operator);
        }
      } else {
        if (isWallAssemblyCoreOperator(operator)) {
          leftCoreOperators.push(operator);
        } else {
          leftFinishOperators.push(operator);
        }
      }
    }
  }

  const rightHalfEdgeOperators = wallAssemblyOperators[rightHalfEdge] ?? [];
  if (rightHalfEdgeOperators.length) {
    for (const rightHalfEdgeOperator of rightHalfEdgeOperators) {
      const operator = rightHalfEdgeOperator.item;
      if (rightHalfEdgeOperator.outside) {
        if (isWallAssemblyCoreOperator(operator)) {
          leftCoreOperatorsFromRight.push(operator);
        } else {
          leftHalfEdgeOperatorsFromRight.push(operator);
        }
      } else {
        if (isWallAssemblyCoreOperator(operator)) {
          rightCoreOperators.push(operator);
        } else {
          rightFinishOperators.push(operator);
        }
      }
    }
  }

  /**
   * 3. Add all the `WallFacade` finish layers to the operators.
   * We simply treat the facade layers as operators that come from any other source.
   *
   * @TODO - not a great pattern. Facades currently apply all layers to both sides of the wall, and are filtered out later.
   * We should be able to apply the facade layers to the correct side of the wall.
   */

  const leftFacadeOperators = collectWallSideFacadeOperators(
    leftWallRoom,
    leftWallFacades,
    wallDatums,
    facadeSpanStratum,
    facadeSweepStratum,
    componentInstanceChain,
  );
  const rightFacadeOperators = collectWallSideFacadeOperators(
    rightWallRoom,
    rightWallFacades,
    wallDatums,
    facadeSpanStratum,
    facadeSweepStratum,
    componentInstanceChain,
  );

  for (const operator of leftFacadeOperators) {
    if (isWallAssemblyCoreOperator(operator)) {
      leftCoreOperators.push(operator);
      rightCoreOperatorsFromLeft.push(operator);
    } else {
      leftFinishOperators.push(operator);
      rightFinishOperatorsFromLeft.push(operator);
    }
  }

  for (const operator of rightFacadeOperators) {
    if (isWallAssemblyCoreOperator(operator)) {
      rightCoreOperators.push(operator);
      leftCoreOperatorsFromRight.push(operator);
    } else {
      rightFinishOperators.push(operator);
      leftHalfEdgeOperatorsFromRight.push(operator);
    }
  }

  /**
   * 4. Merge operators - opposite sides operators are applied first and are overridden
   * by the local operators
   */
  const finalLeftCoreOperators = [...leftCoreOperatorsFromRight, ...leftCoreOperators];
  const finalRightCoreOperators = [...rightCoreOperatorsFromLeft, ...rightCoreOperators];

  const finalLeftFinishOperators = [...leftHalfEdgeOperatorsFromRight, ...leftFinishOperators];
  const finalRightFinishOperators = [...rightFinishOperatorsFromLeft, ...rightFinishOperators];

  return {
    leftCoreOperators: finalLeftCoreOperators,
    rightCoreOperators: finalRightCoreOperators,
    leftFinishOperators: finalLeftFinishOperators,
    rightFinishOperators: finalRightFinishOperators,
  };
}

/**
 * Converts WallFacadeSpans to WallAssemblyLayerOperators
 * @TODO - facades should store their layers as operators, and not be converted here.
 */
function collectWallSideFacadeOperators(
  wallSideRoom: WallSideRoomDescriptor,
  wallSideFacades: Facade[],
  wallDatums: Table<WallDatumAbstraction>,
  facadeSpanStratum: Table<FacadeSpanStratum>,
  facadeSweepStratum: Table<FacadeSweepStratum>,
  componentInstanceChain?: string[],
) {
  const facadeOperators: WallAssemblyLayerOperator[] = [];

  const wallFacadeSpans: FacadeSpanStratum[] = [];
  const wallFacadeSweeps: FacadeSweepStratum[] = [];

  for (const facade of wallSideFacades) {
    const { id: facadeId } = facade;
    wallFacadeSpans.push(...docEntityGetSortedChildrenFromTable(facadeSpanStratum, facadeId));
    wallFacadeSweeps.push(...docEntityGetSortedChildrenFromTable(facadeSweepStratum, facadeId));
  }

  const wallFacadeDatums = collectWallFacadeDatums(
    wallDatums,
    wallFacadeSpans,
    wallFacadeSweeps,
    componentInstanceChain,
  );

  for (const stratum of wallFacadeSpans) {
    const { parentId: facadeId } = stratum;
    if (!facadeId) {
      continue;
    }

    const { material, materialTransformation, materialThickness, elevationNoteEnabled, elevationNote, id } = stratum;
    const { lowerDatumUsage, upperDatumUsage, startDatumUsage, endDatumUsage } = wallAssemblyDatumsFromFacadeSpan(
      stratum,
      wallFacadeDatums,
    );

    facadeOperators.push(
      wallAssemblyLayerOperatorAddNew(
        wallAssemblyExteriorFinishLayerNew({
          finishMaterial: material,
          lowerDatumUsage,
          upperDatumUsage,
          startDatumUsage,
          endDatumUsage,
          materialTransformation: materialTransformation
            ? {
                ...materialTransformation,
                uTranslation: materialTransformation?.uTranslation ?? 0,
                vTranslation: materialTransformation?.vTranslation ?? 0,
                rotationDegrees: materialTransformation?.rotationDegrees ?? 0,
              }
            : undefined,
          materialThickness,
          elevationNoteEnabled,
          elevationNote,
          roomId: wallSideRoom.room?.id,
          layerSource: { type: "FACADE_SPAN_STRATUM", id },
        }),
      ),
    );
  }

  return facadeOperators;
}

interface ApplyWallAssemblyOperatorsOptions {
  level: Level;
  wallDatumAbstractionProvider: WallDatumAbstractionProvider;
  wallPath: WallPath;
  wallDescriptor: WallDescriptor;
  wallSection: WallSection;
  wallAssembly: WallAssembly;
  leftOperators?: WallAssemblyOperator[];
  rightOperators?: WallAssemblyOperator[];
  wallMaterials: StructuralMaterialTable;
}

export function applyWallAssemblyOperatorsToWallAssembly({
  level,
  wallDatumAbstractionProvider,
  wallPath,
  wallDescriptor,
  wallSection,
  wallAssembly,
  leftOperators = [],
  rightOperators = [],
  wallMaterials,
}: ApplyWallAssemblyOperatorsOptions): WallAssembly {
  wallAssembly = applyWallAssemblyStructuralOperators({ wallMaterials, wallAssembly, leftOperators, rightOperators });
  wallAssembly = applyWallAssemblyCladdingOperators({
    level,
    wallDatumAbstractionProvider,
    wallPath,
    wallDescriptor,
    wallSection,
    wallAssembly,
    operators: leftOperators,
    isLeftSide: true,
  });
  wallAssembly = applyWallAssemblyCladdingOperators({
    level,
    wallDatumAbstractionProvider,
    wallPath,
    wallDescriptor,
    wallSection,
    wallAssembly,
    operators: rightOperators,
    isLeftSide: false,
  });

  return wallAssembly;
}

interface ApplyWallAssemblyStructuralOperatorOptions {
  wallMaterials: StructuralMaterialTable;
  wallAssembly: WallAssembly;
  leftOperators?: WallAssemblyOperator[];
  rightOperators?: WallAssemblyOperator[];
}

export function applyWallAssemblyStructuralOperators({
  wallMaterials,
  wallAssembly,
  leftOperators = [],
  rightOperators = [],
}: ApplyWallAssemblyStructuralOperatorOptions) {
  const leftOffsetOperators = leftOperators.filter(isWallAssemblyOffsetOperator);
  const leftStructuralOperators = leftOperators
    .filter(isWallAssemblyLayerOperator)
    .filter((o) => o.layer.type === WallMaterialType.STRUCTURE);
  const rightOffsetOperators = rightOperators.filter(isWallAssemblyOffsetOperator);
  const rightStructuralOperators = rightOperators
    .filter(isWallAssemblyLayerOperator)
    .filter((o) => o.layer.type === WallMaterialType.STRUCTURE);

  const getWallAssemblyOverrideStructuralThickness = (operators: WallAssemblyLayerOperator[]) => {
    return operators.reduce((thickness, operator) => {
      if (!isWallAssemblyLayerRemoveOperator(operator) && operator.layer.type === WallMaterialType.STRUCTURE) {
        thickness += operator.layer.wallMaterialId ? wallMaterials[operator.layer.wallMaterialId]?.thickness : 0;
      }
      return thickness;
    }, 0);
  };

  function getWallAssemblyOverrideStructuralOffset(operators: WallAssemblyOffsetOperator[]) {
    return operators.reduce((sum, { offset }) => {
      sum += offset;
      return sum;
    }, 0);
  }

  const leftStructuralOverrideThickness = getWallAssemblyOverrideStructuralThickness(leftStructuralOperators);
  const leftStructuralLayersWithOverride = applyWallAssemblyLayerOperators(
    wallAssembly.structuralLayers,
    leftStructuralOperators,
  );
  const leftStructuralThicknessWithOverrides = accumWallAssemblyLayerThickness(
    wallMaterials,
    leftStructuralLayersWithOverride,
  );

  const rightStructuralOverrideThickness = getWallAssemblyOverrideStructuralThickness(rightStructuralOperators);
  const rightStructuralLayersWithOverride = applyWallAssemblyLayerOperators(
    wallAssembly.structuralLayers,
    rightStructuralOperators,
  );
  const rightStructuralThicknessWithOverrides = accumWallAssemblyLayerThickness(
    wallMaterials,
    rightStructuralLayersWithOverride,
  );

  // structural layer overrides are based on the OVERRIDE thickness only
  let structuralLayers: WallAssemblyStructuralLayer[];
  if (Math.abs(leftStructuralOverrideThickness - rightStructuralOverrideThickness) < DIST_EPSILON) {
    // default left if exists then right
    structuralLayers = leftOperators.length ? leftStructuralLayersWithOverride : rightStructuralLayersWithOverride;
  } else if (leftStructuralOverrideThickness > rightStructuralOverrideThickness) {
    structuralLayers = leftStructuralLayersWithOverride;
  } else {
    structuralLayers = rightStructuralLayersWithOverride;
  }

  // structural priority is based on the RESOLVED structural thickness
  let structuralPriority: PathPriority;
  if (Math.abs(leftStructuralThicknessWithOverrides - rightStructuralThicknessWithOverrides) < DIST_EPSILON) {
    structuralPriority = PathPriority.NONE;
  } else if (leftStructuralThicknessWithOverrides > rightStructuralThicknessWithOverrides) {
    structuralPriority = PathPriority.LEFT;
  } else {
    structuralPriority = PathPriority.RIGHT;
  }

  const leftStructuralOffset = getWallAssemblyOverrideStructuralOffset(leftOffsetOperators);
  const rightStructuralOffset = -getWallAssemblyOverrideStructuralOffset(rightOffsetOperators);
  const structuralOffset = leftStructuralOffset + rightStructuralOffset;

  // if no structural layers are available we revert to the original assembly
  // @NOTE - this is not a good pattern. it prevents empty structural assemblies
  if (structuralLayers.length === 0) {
    // ... ignore the override and use the default structural layers
    structuralLayers = wallAssembly.structuralLayers.slice();
  }

  return wallAssemblyNew({
    ...wallAssembly,
    structuralLayers,
    structuralOffset,
    structuralPriority,
  });
}

interface ApplyWallAssemblyCladdingOperatorOptions {
  level: Level;
  wallDatumAbstractionProvider: WallDatumAbstractionProvider;
  wallPath: WallPath;
  wallDescriptor: WallDescriptor;
  wallSection: WallSection;
  wallAssembly: WallAssembly;
  operators?: WallAssemblyOperator[];
  isLeftSide: boolean;
}

function applyWallAssemblyCladdingOperators({
  level,
  wallDatumAbstractionProvider,
  wallPath,
  wallDescriptor,
  wallSection,
  wallAssembly,
  operators = [],
  isLeftSide,
}: ApplyWallAssemblyCladdingOperatorOptions): WallAssembly {
  const claddingOperators = operators
    .filter(isWallAssemblyLayerOperator)
    .filter((o) => o.layer.type !== WallMaterialType.STRUCTURE);

  const srcCladdingLayers = isLeftSide ? wallAssembly.leftCladdingLayers : wallAssembly.rightCladdingLayers;
  const srcExtraFinishLayers = isLeftSide ? wallAssembly.extraLeftLayers : wallAssembly.extraRightLayers;

  /**
   * Layer Operator application happens in 2 phases.
   * - Apply the operators to the layers, which may change bounds/materialThickness/etc...
   * - Filter the new layers by whether they can be added with the overrides applied
   */

  const claddingLayers = applyWallAssemblyLayerOperators(
    srcCladdingLayers,
    claddingOperators.filter(isWallAssemblyLayerOverrideOperator),
  ).filter((layer) =>
    wallAssemblyCanApplyLayer(
      level,
      wallDatumAbstractionProvider,
      wallPath,
      wallDescriptor,
      wallSection,
      layer,
      isLeftSide,
    ),
  );

  const extraFinishLayers = applyWallAssemblyLayerOperators(
    srcExtraFinishLayers,
    claddingOperators.filter(isWallAssemblyLayerAddOperator),
  ).filter((layer) =>
    wallAssemblyCanApplyLayer(
      level,
      wallDatumAbstractionProvider,
      wallPath,
      wallDescriptor,
      wallSection,
      layer,
      isLeftSide,
    ),
  );

  return wallAssemblyNew({
    ...wallAssembly,
    leftCladdingLayers: isLeftSide ? claddingLayers : wallAssembly.leftCladdingLayers,
    extraLeftLayers: isLeftSide ? extraFinishLayers : wallAssembly.extraLeftLayers,
    rightCladdingLayers: isLeftSide ? wallAssembly.rightCladdingLayers : claddingLayers,
    extraRightLayers: isLeftSide ? wallAssembly.extraRightLayers : extraFinishLayers,
  });
}

/**
 * applyWallAssemblyOperators functions similar to a reducer, but on an array of wallAssemblyLayers.
 * Each WallAssemblyOperator is the equivalent of an action, and will mutate the layer array and return a copy.
 */
function applyWallAssemblyLayerOperators<T extends WallAssemblyLayer>(
  wallAssemblyLayers: T[],
  wallAssemblyLayerOperators: WallAssemblyLayerOperator[],
) {
  for (const operator of wallAssemblyLayerOperators) {
    switch (operator.operationType) {
      case "ADD": {
        wallAssemblyLayers = [...wallAssemblyLayers, wallAssemblyLayerNewFromConstructor(operator.layer) as T]; // TODO - casting no good
        break;
      }
      case "REMOVE": {
        const { layer, layerIndex } = operator;
        const layersFiltered = wallAssemblyLayers
          .map((l, i) => ({ index: i, layer: l as T })) // TODO - casting no good
          .filter((l) => l.layer.type === layer.type);
        const layerTarget = layersFiltered[layerIndex];
        if (!layerTarget) {
          break;
        }
        const layersCopy: (T | null)[] = wallAssemblyLayers.slice();
        layersCopy[layerTarget.index] = null;
        wallAssemblyLayers = layersCopy.filter((l) => l !== null);
        break;
      }
      case "OVERRIDE": {
        const { layer, layerIndex } = operator;
        const layersFiltered = wallAssemblyLayers
          .map((l, i) => ({ index: i, layer: l as T })) // TODO - casting no good
          .filter((l) => l.layer.type === layer.type);

        const layerTarget = layersFiltered[layerIndex];
        if (!layerTarget) {
          break;
        }
        const layersCopy = wallAssemblyLayers.slice();
        const layerOverride = { ...layerTarget.layer, ...operator.layer };
        layersCopy[layerTarget.index] = layerOverride;
        wallAssemblyLayers = layersCopy;
        break;
      }
      default: {
        assertUnreachable(operator);
      }
    }
  }
  return wallAssemblyLayers;
}
